import { paymentService, Invoice, PaymentMethod } from './paymentService';
import { subscriptionService, UserSubscription } from './subscriptionService';
import { CURRENCY_SYMBOLS } from '../config/stripeConfig';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  Timestamp,
  limit as firestoreLimit,
  orderBy,
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { planPricingService } from './planPricingService';
import type { CompleteDodoCheckoutResult } from './dodoPaymentService';

// Types for billing operations
export interface BillingInfo {
  customerId: string;
  subscription: UserSubscription | null;
  paymentMethods: PaymentMethod[];
  invoices: Invoice[];
  nextBillingDate: Date | null;
  lastPaymentDate: Date | null;
  totalSpent: number;
  currency: string;
}

export interface BillingSummary {
  currentPlan: string;
  status: string;
  nextBillingDate: string;
  amount: string;
  currency: string;
  paymentMethod: string;
}

export interface UsageStats {
  emailsSent: number;
  emailsRemaining: number;
  subscribersCount: number;
  subscribersLimit: number;
  usagePercentage: number;
}

class BillingService {
  /** Same as `firestoreSafeDocId` in `functions/dodoPayments.js` (invoice document ids). */
  private dodoFirestoreSafeDocId(s: string): string {
    return String(s).replace(/[/:]/g, '_');
  }

  /** Same as `yyyyMmDdUtc` in `functions/dodoPayments.js` (subscription invoice doc suffix). */
  private yyyyMmDdUtc(dateLike: Date | null | undefined): string {
    try {
      const d =
        dateLike instanceof Date
          ? dateLike
          : dateLike
            ? new Date(dateLike as unknown as string)
            : new Date();
      if (Number.isNaN(d.getTime())) return 'na';
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${y}${m}${day}`;
    } catch {
      return 'na';
    }
  }

  private isDodoSubscription(sub: UserSubscription): boolean {
    if (sub.paymentProvider === 'dodo') return true;
    const sid = sub.stripeSubscriptionId;
    return typeof sid === 'string' && sid.startsWith('dodo_');
  }

  /** Server syncs every `invoices/` row from live Dodo charge totals via Admin SDK. */
  async ensureDodoInvoiceViaCloudFunction(invoiceId?: string): Promise<{
    ok: boolean;
    amountPaidMinor?: number | null;
    amountMajor?: number | null;
    currency?: string | null;
    invoices?: Array<{
      invoiceId: string;
      amountPaidMinor?: number | null;
      amountMajor?: number | null;
      currency?: string | null;
      paymentId?: string | null;
    }>;
  }> {
    try {
      const fn = httpsCallable<
        { invoiceId?: string },
        {
          ok?: boolean;
          amountPaidMinor?: number | null;
          amountMajor?: number | null;
          currency?: string | null;
          invoices?: Array<{
            invoiceId: string;
            amountPaidMinor?: number | null;
            amountMajor?: number | null;
            currency?: string | null;
            paymentId?: string | null;
          }>;
        }
      >(functions, 'ensureMyDodoInvoice');
      const res = await fn(invoiceId ? { invoiceId } : {});
      const data = res.data || {};
      return {
        ok: !!data.ok,
        amountPaidMinor: data.amountPaidMinor ?? null,
        amountMajor: data.amountMajor ?? null,
        currency: data.currency ?? null,
        invoices: data.invoices ?? [],
      };
    } catch (e) {
      console.warn('ensureMyDodoInvoice callable unavailable:', e);
      return { ok: false, invoices: [] };
    }
  }

  /** Fetch invoice PDF from Dodo (authoritative totals) and cache in Storage. */
  async getMyInvoicePdf(invoiceId: string): Promise<{
    ok: boolean;
    pdfUrl?: string | null;
    paymentId?: string | null;
    dodoInvoiceUrl?: string | null;
    amountPaidMinor?: number | null;
    amountMajor?: number | null;
    currency?: string | null;
    reason?: string;
  }> {
    try {
      const fn = httpsCallable<
        { invoiceId: string },
        {
          ok?: boolean;
          pdfUrl?: string | null;
          paymentId?: string | null;
          dodoInvoiceUrl?: string | null;
          amountPaidMinor?: number | null;
          amountMajor?: number | null;
          currency?: string | null;
          reason?: string;
        }
      >(functions, 'getMyInvoicePdf');
      const res = await fn({ invoiceId });
      const data = res.data || {};
      return {
        ok: !!data.ok,
        pdfUrl: data.pdfUrl ?? null,
        paymentId: data.paymentId ?? null,
        dodoInvoiceUrl: data.dodoInvoiceUrl ?? null,
        amountPaidMinor: data.amountPaidMinor ?? null,
        amountMajor: data.amountMajor ?? null,
        currency: data.currency ?? null,
        reason: data.reason,
      };
    } catch (e) {
      console.warn('getMyInvoicePdf callable unavailable:', e);
      return { ok: false, reason: 'callable_error' };
    }
  }

  /** Resolve live Dodo charge for one invoice owned by the signed-in user. */
  async resolveInvoiceCharge(invoiceId: string): Promise<{
    ok: boolean;
    amountPaidMinor?: number | null;
    amountMajor?: number | null;
    currency?: string | null;
    paymentId?: string | null;
    reason?: string;
  }> {
    try {
      const fn = httpsCallable<
        { invoiceId: string },
        {
          ok?: boolean;
          amountPaidMinor?: number | null;
          amountMajor?: number | null;
          currency?: string | null;
          paymentId?: string | null;
          reason?: string;
        }
      >(functions, 'resolveMyInvoiceCharge');
      const res = await fn({ invoiceId });
      const data = res.data || {};
      return {
        ok: !!data.ok,
        amountPaidMinor: data.amountPaidMinor ?? null,
        amountMajor: data.amountMajor ?? null,
        currency: data.currency ?? null,
        paymentId: data.paymentId ?? null,
        reason: data.reason,
      };
    } catch (e) {
      console.warn('resolveMyInvoiceCharge callable unavailable:', e);
      return { ok: false, reason: 'callable_error' };
    }
  }

  /**
   * If `completeDodoCheckout` succeeded but the `invoices` row is missing (race, partial deploy, etc.),
   * write the same shape the Cloud Function uses, with a deterministic doc id so this is idempotent.
   */
  async ensureDodoInvoiceFromCheckoutResult(
    userId: string,
    result: CompleteDodoCheckoutResult
  ): Promise<void> {
    if (!result?.ok) return;

    const sub = await subscriptionService.getUserSubscription(userId).catch(() => null);

    let invoiceDocId =
      typeof result.invoiceId === 'string' && result.invoiceId.trim() ? result.invoiceId.trim() : '';

    const payFromResult =
      typeof result.paymentId === 'string' && result.paymentId.trim() ? result.paymentId.trim() : '';

    const dodoSubFromResult =
      typeof result.dodoSubscriptionId === 'string' && result.dodoSubscriptionId.trim()
        ? result.dodoSubscriptionId.trim()
        : '';

    const payFromSub = sub?.dodoPaymentId && String(sub.dodoPaymentId).trim() ? String(sub.dodoPaymentId).trim() : '';
    const lastRef = sub?.lastPaymentReference && String(sub.lastPaymentReference).trim();
    const payFromLast =
      lastRef && !lastRef.startsWith('pay_') && !lastRef.startsWith('pi_') ? lastRef : '';

    const dodoSubFromUser =
      sub?.dodoSubscriptionId && String(sub.dodoSubscriptionId).trim()
        ? String(sub.dodoSubscriptionId).trim()
        : '';

    const stripeTail =
      sub?.stripeSubscriptionId && String(sub.stripeSubscriptionId).startsWith('dodo_')
        ? String(sub.stripeSubscriptionId).slice(5).trim()
        : '';

    let payId = payFromResult || payFromSub || payFromLast || '';
    let dodoSubscriptionId = dodoSubFromResult || dodoSubFromUser;

    if (!payId && !dodoSubscriptionId && stripeTail) {
      if (stripeTail.toLowerCase().startsWith('pay')) {
        payId = stripeTail;
      } else {
        dodoSubscriptionId = stripeTail;
      }
    }

    if (!invoiceDocId && payId) {
      invoiceDocId = `inv_dodo_pay_${this.dodoFirestoreSafeDocId(payId)}`;
    }
    if (!invoiceDocId && dodoSubscriptionId) {
      const anchor = this.yyyyMmDdUtc(sub?.currentPeriodEnd ?? null);
      invoiceDocId = `inv_dodo_sub_${this.dodoFirestoreSafeDocId(dodoSubscriptionId)}_${anchor}`;
    }
    if (!invoiceDocId) return;

    const ref = doc(db, 'invoices', invoiceDocId);

    // Avoid a second row if Cloud Function used a different doc id but same Dodo payment / subscription.
    try {
      const recent = await getDocs(
        query(collection(db, 'invoices'), where('userId', '==', userId), firestoreLimit(80))
      );
      if (
        payId &&
        recent.docs.some((d) => {
          const row = d.data();
          return row?.paymentId === payId || row?.subscriptionId === `dodo_${payId}`;
        })
      ) {
        return;
      }
      if (
        dodoSubscriptionId &&
        recent.docs.some((d) => d.data()?.dodoSubscriptionId === dodoSubscriptionId)
      ) {
        return;
      }
    } catch {
      // continue — if listing fails, still try setDoc
    }

    const amountUsd =
      typeof result.amountUsd === 'number' && Number.isFinite(result.amountUsd) && result.amountUsd > 0
        ? result.amountUsd
        : NaN;
    const planKey = ((result.plan || sub?.plan || '') as string).toLowerCase();
    const subs = result.subscribers != null ? result.subscribers : sub?.subscribers;
    const billingPeriod = (result.billingPeriod || sub?.billingPeriod || 'monthly') as 'monthly' | 'annual';

    if (!Number.isFinite(amountUsd) || amountUsd <= 0) {
      return;
    }
    const cents = Math.max(0, Math.round(amountUsd * 100));
    if (cents <= 0) return;

    const currency = (sub?.currency || 'usd').toLowerCase();
    const subscriptionIdField = payId
      ? `dodo_${payId}`
      : dodoSubscriptionId
        ? `dodo_sub_${dodoSubscriptionId}`
        : sub?.stripeSubscriptionId || null;

    await setDoc(
      ref,
      {
        userId,
        customer: userId,
        amount_paid: cents,
        amount_due: 0,
        currency,
        status: 'paid',
        created: Timestamp.now(),
        subscriptionId: subscriptionIdField,
        dodoSubscriptionId: dodoSubscriptionId || null,
        plan: planKey || sub?.plan || null,
        subscribers: subs != null ? Number(subs) : sub?.subscribers ?? null,
        billingPeriod,
        paymentId: payId || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  }

  /** When Firestore has no `invoices` rows yet but the user is on Dodo, sync via callable then client fallback. */
  private async backfillDodoInvoiceIfMissing(userId: string, subscription: UserSubscription): Promise<void> {
    if (!this.isDodoSubscription(subscription)) return;

    const serverOk = await this.ensureDodoInvoiceViaCloudFunction();
    if (serverOk.ok) return;

    await this.ensureDodoInvoiceFromCheckoutResult(userId, {
      ok: true,
      paymentId: subscription.dodoPaymentId || undefined,
      dodoSubscriptionId: subscription.dodoSubscriptionId || undefined,
      plan: subscription.plan,
      subscribers: subscription.subscribers,
      billingPeriod: subscription.billingPeriod,
    });
  }

  // Get comprehensive billing information for a user
  async getBillingInfo(userId: string): Promise<BillingInfo> {
    try {
      // Always try local invoices first so billing history is visible
      // even when subscription docs are missing/out-of-sync.
      let localInvoices = await this.getLocalInvoices(userId);

      // Get user subscription
      const subscription = await subscriptionService.getUserSubscription(userId);

      // For Dodo, always allow server-side reconciliation to correct stale invoice amounts/dates.
      if (subscription && this.isDodoSubscription(subscription)) {
        try {
          const reconciled = await this.ensureDodoInvoiceViaCloudFunction();
          if (reconciled.ok) {
            localInvoices = await this.getLocalInvoices(userId);
          }
        } catch (e) {
          console.warn('getBillingInfo: Dodo invoice reconcile failed:', e);
        }
      }

      if (subscription && localInvoices.length === 0) {
        try {
          await this.backfillDodoInvoiceIfMissing(userId, subscription);
          localInvoices = await this.getLocalInvoices(userId);
        } catch (e) {
          console.warn('getBillingInfo: Dodo invoice backfill failed:', e);
        }
      }

      if (!subscription) {
        const totalSpent = localInvoices
          .filter(invoice => invoice.status === 'paid')
          .reduce((sum, invoice) => sum + (invoice.amount_paid || 0), 0) / 100;

        const latestPaidInvoice = localInvoices
          .filter(invoice => invoice.status === 'paid')
          .sort((a, b) => b.created - a.created)[0];

        return {
          customerId: '',
          subscription: null,
          paymentMethods: [],
          invoices: localInvoices,
          nextBillingDate: null,
          lastPaymentDate: latestPaidInvoice ? new Date(latestPaidInvoice.created * 1000) : null,
          totalSpent,
          currency: localInvoices[0]?.currency || 'usd',
        };
      }

      // Return billing info even without Stripe customer (for local subscriptions)
      if (!subscription.stripeCustomerId) {
        // Calculate total spent from local invoices
        const totalSpent = localInvoices
          .filter(invoice => invoice.status === 'paid')
          .reduce((sum, invoice) => sum + (invoice.amount_paid || 0), 0) / 100;

        // Get last payment date
        const lastPaymentDate = localInvoices
          .filter(invoice => invoice.status === 'paid')
          .sort((a, b) => b.created - a.created)[0]?.created
          ? new Date(localInvoices
            .filter(invoice => invoice.status === 'paid')
            .sort((a, b) => b.created - a.created)[0].created * 1000)
          : null;

        return {
          customerId: '',
          subscription: subscription,
          paymentMethods: [],
          invoices: localInvoices,
          nextBillingDate: subscription.currentPeriodEnd || null,
          lastPaymentDate: lastPaymentDate,
          totalSpent: totalSpent,
          currency: subscription.currency || 'usd',
        };
      }

      // Get payment methods and invoices in parallel (Stripe + local)
      // Wrap Stripe calls in try/catch so local invoices still load if Stripe fails
      let stripePaymentMethods: any[] = [];
      let stripeInvoices: any[] = [];
      let mergedLocalInvoices: any[] = localInvoices;

      try {
        [stripePaymentMethods, stripeInvoices, mergedLocalInvoices] = await Promise.all([
          paymentService.getPaymentMethods(subscription.stripeCustomerId).catch(() => []),
          paymentService.getInvoices(subscription.stripeCustomerId, 10).catch(() => []),
          this.getLocalInvoices(userId),
        ]);
      } catch (error) {
        console.error('Error fetching billing data:', error);
        // At minimum, try to get local invoices
        mergedLocalInvoices = await this.getLocalInvoices(userId).catch(() => []);
      }

      const paymentMethods = stripePaymentMethods;

      // Merge Stripe and local invoices, deduplicating by ID
      const invoiceMap = new Map<string, any>();
      for (const inv of stripeInvoices) {
        invoiceMap.set(inv.id, inv);
      }
      for (const inv of mergedLocalInvoices) {
        if (!invoiceMap.has(inv.id)) {
          invoiceMap.set(inv.id, inv);
        }
      }
      let invoices = Array.from(invoiceMap.values()).sort((a: any, b: any) => b.created - a.created);

      if (invoices.length === 0 && this.isDodoSubscription(subscription)) {
        try {
          await this.backfillDodoInvoiceIfMissing(userId, subscription);
          const after = await this.getLocalInvoices(userId);
          for (const inv of after) {
            if (!invoiceMap.has(inv.id)) {
              invoiceMap.set(inv.id, inv);
            }
          }
          invoices = Array.from(invoiceMap.values()).sort((a: any, b: any) => b.created - a.created);
        } catch (e) {
          console.warn('getBillingInfo: Dodo invoice backfill (Stripe branch) failed:', e);
        }
      }

      // Calculate total spent
      const totalSpent = invoices
        .filter(invoice => invoice.status === 'paid')
        .reduce((sum, invoice) => sum + invoice.amount_paid, 0) / 100; // Convert from cents

      // Get next billing date
      const nextBillingDate = subscription.status === 'active'
        ? subscription.currentPeriodEnd
        : null;

      // Get last payment date
      const lastPaymentDate = invoices
        .filter(invoice => invoice.status === 'paid')
        .sort((a, b) => b.created - a.created)[0]?.created
        ? new Date(invoices
          .filter(invoice => invoice.status === 'paid')
          .sort((a, b) => b.created - a.created)[0].created * 1000)
        : null;

      return {
        customerId: subscription.stripeCustomerId,
        subscription,
        paymentMethods,
        invoices,
        nextBillingDate,
        lastPaymentDate,
        totalSpent,
        currency: subscription.currency,
      };
    } catch (error) {
      console.error('Error getting billing info:', error);
      throw new Error('Failed to get billing information');
    }
  }

  // Get billing summary for dashboard display
  async getBillingSummary(userId: string): Promise<BillingSummary> {
    try {
      const billingInfo = await this.getBillingInfo(userId);

      if (!billingInfo.subscription) {
        return {
          currentPlan: 'No Plan',
          status: 'Inactive',
          nextBillingDate: 'N/A',
          amount: '$0',
          currency: 'usd',
          paymentMethod: 'None',
        };
      }

      const subscription = billingInfo.subscription;
      const statusInfo = subscriptionService.getSubscriptionStatus(subscription);

      // Get default payment method
      const defaultPaymentMethod = billingInfo.paymentMethods.find(pm => pm.is_default);
      const paymentMethodInfo = defaultPaymentMethod?.card
        ? `${defaultPaymentMethod.card.brand.toUpperCase()} •••• ${defaultPaymentMethod.card.last4}`
        : 'No payment method';

      // Calculate next billing amount
      const nextBillingAmount = subscription.plan === 'starter'
        ? 0
        : await planPricingService.getPriceForSubscribers(subscription.plan as any, subscription.subscribers);

      const planKey = (subscription.plan || '').toLowerCase();
      const currentPlanLabel =
        planKey === 'starter' || planKey === 'free'
          ? 'Starter (Free)'
          : planKey === 'business'
            ? 'Business'
            : planKey === 'pro'
              ? 'Pro'
              : subscription.plan || 'Paid';

      return {
        currentPlan: currentPlanLabel,
        status: statusInfo.status,
        nextBillingDate: billingInfo.nextBillingDate?.toLocaleDateString() || 'N/A',
        amount: `${CURRENCY_SYMBOLS[subscription.currency as keyof typeof CURRENCY_SYMBOLS] || '$'}${nextBillingAmount}`,
        currency: subscription.currency,
        paymentMethod: paymentMethodInfo,
      };
    } catch (error) {
      console.error('Error getting billing summary:', error);
      throw new Error('Failed to get billing summary');
    }
  }

  // Get usage statistics
  async getUsageStats(userId: string): Promise<UsageStats> {
    try {
      const limits = await subscriptionService.checkSubscriptionLimits(userId);

      return {
        emailsSent: limits.usedEmails,
        emailsRemaining: limits.remainingEmails,
        subscribersCount: 0, // You would get this from your contacts service
        subscribersLimit: limits.maxSubscribers,
        usagePercentage:
          Number.isFinite(limits.maxEmails) && limits.maxEmails > 0
            ? Math.round((limits.usedEmails / limits.maxEmails) * 100)
            : 0,
      };
    } catch (error) {
      console.error('Error getting usage stats:', error);
      throw new Error('Failed to get usage statistics');
    }
  }

  // Add a new payment method
  async addPaymentMethod(userId: string, paymentMethodId: string): Promise<PaymentMethod> {
    try {
      const subscription = await subscriptionService.getUserSubscription(userId);

      if (!subscription?.stripeCustomerId) {
        throw new Error('No customer found. Please create a subscription first.');
      }

      return await paymentService.addPaymentMethod(subscription.stripeCustomerId, paymentMethodId);
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw new Error('Failed to add payment method');
    }
  }

  // Remove a payment method
  async removePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      await paymentService.removePaymentMethod(paymentMethodId);
    } catch (error) {
      console.error('Error removing payment method:', error);
      throw new Error('Failed to remove payment method');
    }
  }

  // Update default payment method
  async setDefaultPaymentMethod(userId: string, paymentMethodId: string): Promise<void> {
    try {
      const subscription = await subscriptionService.getUserSubscription(userId);

      if (!subscription?.stripeCustomerId) {
        throw new Error('No customer found');
      }

      // This would typically involve updating the customer's default source in Stripe
      // For now, we'll just update our local data
      console.log('Setting default payment method:', paymentMethodId);
    } catch (error) {
      console.error('Error setting default payment method:', error);
      throw new Error('Failed to set default payment method');
    }
  }

  // Get invoice details
  async getInvoiceDetails(invoiceId: string): Promise<Invoice | null> {
    try {
      // In a real implementation, you would fetch this from Stripe
      // For now, return null
      return null;
    } catch (error) {
      console.error('Error getting invoice details:', error);
      return null;
    }
  }

  // Download invoice PDF
  async downloadInvoice(invoiceId: string): Promise<string | null> {
    try {
      // In a real implementation, you would get the PDF URL from Stripe
      // For now, return null
      return null;
    } catch (error) {
      console.error('Error downloading invoice:', error);
      return null;
    }
  }

  // Calculate monthly price for Pro plan
  private async calculateMonthlyPrice(subscribers: number, plan: 'pro' | 'business' = 'pro'): Promise<number> {
    return await planPricingService.getPriceForSubscribers(plan, subscribers);
  }

  // Get billing history with pagination
  async getBillingHistory(userId: string, page: number = 1, limit: number = 10): Promise<{
    invoices: Invoice[];
    hasMore: boolean;
    total: number;
  }> {
    try {
      const subscription = await subscriptionService.getUserSubscription(userId);

      if (!subscription?.stripeCustomerId) {
        // Return local invoices for non-Stripe subscriptions
        const localInvoices = await this.getLocalInvoices(userId);
        return {
          invoices: localInvoices.slice(0, limit),
          hasMore: localInvoices.length > limit,
          total: localInvoices.length,
        };
      }

      const invoices = await paymentService.getInvoices(subscription.stripeCustomerId, limit);

      return {
        invoices,
        hasMore: invoices.length === limit,
        total: invoices.length, // In a real implementation, you'd get the total count
      };
    } catch (error) {
      console.error('Error getting billing history:', error);
      throw new Error('Failed to get billing history');
    }
  }

  // Check if user needs to update payment method
  async needsPaymentMethodUpdate(userId: string): Promise<boolean> {
    try {
      const billingInfo = await this.getBillingInfo(userId);

      if (!billingInfo.subscription || billingInfo.subscription.plan === 'starter') {
        return false;
      }

      // Check if subscription is past due or has no payment methods
      return billingInfo.subscription.status === 'past_due' ||
        billingInfo.paymentMethods.length === 0;
    } catch (error) {
      console.error('Error checking payment method update need:', error);
      return false;
    }
  }

  // Get upcoming charges
  async getUpcomingCharges(userId: string): Promise<{
    amount: number;
    currency: string;
    date: Date;
    description: string;
  } | null> {
    try {
      const billingInfo = await this.getBillingInfo(userId);

      if (!billingInfo.subscription ||
        billingInfo.subscription.plan === 'starter' ||
        billingInfo.subscription.status !== 'active') {
        return null;
      }

      const amount = await this.calculateMonthlyPrice(
        billingInfo.subscription.subscribers,
        billingInfo.subscription.plan as any
      );

      return {
        amount,
        currency: billingInfo.subscription.currency,
        date: billingInfo.nextBillingDate || new Date(),
        description: `Pro Plan - ${billingInfo.subscription.subscribers} subscribers`,
      };
    } catch (error) {
      console.error('Error getting upcoming charges:', error);
      return null;
    }
  }

  // Get local invoices from Firestore (for non-Stripe subscriptions)
  private async getLocalInvoices(userId: string): Promise<Invoice[]> {
    try {
      const invoicesRef = collection(db, 'invoices');
      // Read by both userId and customer because older/newer flows may populate either field.
      const [byUserIdSnap, byCustomerSnap] = await Promise.all([
        getDocs(query(invoicesRef, where('userId', '==', userId), firestoreLimit(50))),
        getDocs(query(invoicesRef, where('customer', '==', userId), firestoreLimit(50))),
      ]);

      const invoiceMap = new Map<string, Invoice>();
      const mapDocToInvoice = (docSnap: any): Invoice => {
        const data = docSnap.data() || {};
        const createdRaw = data.created;
        const created =
          typeof createdRaw?.toMillis === 'function'
            ? createdRaw.toMillis() / 1000
            : typeof createdRaw === 'number'
              ? (createdRaw > 10_000_000_000 ? createdRaw / 1000 : createdRaw)
              : Date.now() / 1000;
        // Include extra fields for non-Stripe invoice generation (Dodo/local).
        return {
          id: docSnap.id,
          customer: data.customer || data.userId || userId,
          amount_paid: data.amount_paid || 0,
          amount_due: data.amount_due || 0,
          currency: data.currency || 'usd',
          status: data.status || 'paid',
          created,
          hosted_invoice_url: data.hosted_invoice_url,
          invoice_pdf: data.invoice_pdf,
          ...(data.plan ? { plan: data.plan } : {}),
          ...(data.subscribers != null ? { subscribers: data.subscribers } : {}),
          ...(data.billingPeriod ? { billingPeriod: data.billingPeriod } : {}),
          ...(data.paymentId ? { paymentId: data.paymentId } : {}),
          ...(data.dodoSubscriptionId ? { dodoSubscriptionId: data.dodoSubscriptionId } : {}),
          ...(data.subscriptionId ? { subscriptionId: data.subscriptionId } : {}),
        };
      };

      byUserIdSnap.forEach((docSnap) => {
        invoiceMap.set(docSnap.id, mapDocToInvoice(docSnap));
      });
      byCustomerSnap.forEach((docSnap) => {
        if (!invoiceMap.has(docSnap.id)) {
          invoiceMap.set(docSnap.id, mapDocToInvoice(docSnap));
        }
      });
      const invoices = Array.from(invoiceMap.values());

      // Sort in-memory by created date descending
      invoices.sort((a, b) => b.created - a.created);

      return invoices;
    } catch (error) {
      console.error('Error getting local invoices:', error);
      return [];
    }
  }

  // Get invoices for a specific user (public method for admin use)
  async getInvoicesForUser(userId: string): Promise<Invoice[]> {
    return this.getLocalInvoices(userId);
  }

  // Get all invoices across all users (admin only)
  async getAllInvoices(
    maxResults: number = 100
  ): Promise<
    (Invoice & {
      userId?: string;
      plan?: string;
      billingPeriod?: string;
      paymentId?: string;
      subscriptionId?: string;
    })[]
  > {
    try {
      const invoicesRef = collection(db, 'invoices');
      const q = query(
        invoicesRef,
        orderBy('created', 'desc'),
        firestoreLimit(maxResults)
      );

      const querySnapshot = await getDocs(q);
      const invoices: (Invoice & {
        userId?: string;
        plan?: string;
        billingPeriod?: string;
        paymentId?: string;
        subscriptionId?: string;
      })[] = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        invoices.push({
          id: docSnap.id,
          customer: data.customer || data.userId || '',
          amount_paid: data.amount_paid || 0,
          amount_due: data.amount_due || 0,
          currency: data.currency || 'usd',
          status: data.status || 'paid',
          created: data.created?.toMillis ? data.created.toMillis() / 1000 : data.created || Date.now() / 1000,
          hosted_invoice_url: data.hosted_invoice_url,
          invoice_pdf: data.invoice_pdf,
          userId: data.userId,
          plan: data.plan,
          billingPeriod: data.billingPeriod,
          paymentId:
            data.paymentId ||
            data.payment_id ||
            (typeof (data as { razorpay_payment_id?: string }).razorpay_payment_id === 'string'
              ? (data as { razorpay_payment_id: string }).razorpay_payment_id
              : undefined),
          subscriptionId:
            typeof (data as { subscriptionId?: string }).subscriptionId === 'string'
              ? (data as { subscriptionId: string }).subscriptionId
              : undefined
        });
      });

      // Sort in-memory by created date descending
      invoices.sort((a, b) => b.created - a.created);

      return invoices;
    } catch (error) {
      console.error('Error getting all invoices:', error);
      return [];
    }
  }

  // Create a local invoice record in Firestore
  async createLocalInvoice(
    userId: string,
    subscription: UserSubscription,
    amount: number,
    currency: string,
    paymentId?: string
  ): Promise<string> {
    try {
      const invoiceId = `SCUBA_INV_${Date.now()}_${userId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8) || 'USER'}`;
      const invoiceRef = doc(db, 'invoices', invoiceId);

      const invoiceData = {
        userId,
        customer: userId,
        amount_paid: Math.round(amount * 100), // Convert to cents
        amount_due: 0,
        currency: currency.toLowerCase(),
        status: 'paid' as const,
        created: Timestamp.now(),
        subscriptionId: subscription.id,
        plan: subscription.plan,
        subscribers: subscription.subscribers,
        billingPeriod: subscription.billingPeriod || 'monthly',
        paymentId: paymentId || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(invoiceRef, invoiceData);
      console.log('✅ Invoice created successfully:', invoiceId);
      return invoiceId;
    } catch (error) {
      console.error('❌ Error creating local invoice:', error);
      throw new Error('Failed to create invoice');
    }
  }
}

export const billingService = new BillingService();
export type { Invoice };
